import { Box, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import {
  CONTACT_CAPTURE_BRAND_NAME_SIZE,
  CONTACT_CAPTURE_FONT_FAMILIES,
  CONTACT_CAPTURE_HEADER_TEXT_SIZE,
} from '../../helpers';
import { ContactCaptureSettings } from '../../types';
import { useBuilder } from './ContactCaptureBuilderContext';
import { ContactCaptureColorField } from './ContactCaptureColorField';
import { ContactCaptureTextStyleControls } from './ContactCaptureTextStyleControls';

const COLOR_FIELDS = [
  { key: 'primaryColor', label: 'Header color' },
  { key: 'buttonColor', label: 'Button color' },
  { key: 'backgroundColor', label: 'Background color' },
  { key: 'textColor', label: 'Text color' },
] as const;

export const ContactCaptureDesignTab = () => {
  const { busy, settings, setSettings, type } = useBuilder();

  const updateStyle = <K extends keyof ContactCaptureSettings['styles']>(
    key: K,
    value: ContactCaptureSettings['styles'][K],
  ) => {
    setSettings(current => ({
      ...current,
      styles: { ...current.styles, [key]: value },
    }));
  };

  const showsHeader = type !== 'inline' || settings.content.showTitle;

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle1" fontWeight="fontWeightSemiBold">Colors and typography</Typography>
        <Typography variant="body2" color="text.secondary">
          Changes appear in the preview immediately.
        </Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
        {COLOR_FIELDS.map(({ key, label }) => (
          <ContactCaptureColorField
            key={key}
            label={label}
            value={settings.styles[key]}
            disabled={busy}
            onChange={value => updateStyle(key, value)}
          />
        ))}
        <TextField
          disabled={busy}
          select
          label="Font family"
          value={settings.styles.fontFamily}
          onChange={event => updateStyle(
            'fontFamily',
            event.target.value as ContactCaptureSettings['styles']['fontFamily'],
          )}
          fullWidth
        >
          {CONTACT_CAPTURE_FONT_FAMILIES.map(font => (
            <MenuItem value={font} key={font} style={{ fontFamily: font }}>{font}</MenuItem>
          ))}
        </TextField>
        <TextField
          disabled={busy}
          type="number"
          label="Corner radius"
          value={settings.styles.borderRadius}
          onChange={event => updateStyle(
            'borderRadius',
            Math.max(0, Math.min(40, Number(event.target.value))),
          )}
          inputProps={{ min: 0, max: 40 }}
          helperText="0–40 pixels"
          fullWidth
        />
        {type === 'popup' && (
          <ContactCaptureColorField
            label="Popup backdrop color"
            value={settings.styles.overlayColor}
            disabled={busy}
            onChange={value => updateStyle('overlayColor', value)}
          />
        )}
      </Box>
      {showsHeader && (
        <>
          <Divider />
          <Stack spacing={1.5}>
            <ContactCaptureTextStyleControls
              label="Brand"
              disabled={busy}
              align={settings.styles.brandNameAlign}
              size={settings.styles.brandNameSize}
              sizeRange={CONTACT_CAPTURE_BRAND_NAME_SIZE}
              bold={settings.styles.brandNameBold}
              italic={settings.styles.brandNameItalic}
              uppercase={settings.styles.brandNameUppercase}
              onAlignChange={value => updateStyle('brandNameAlign', value)}
              onSizeChange={value => updateStyle('brandNameSize', value)}
              onBoldChange={value => updateStyle('brandNameBold', value)}
              onItalicChange={value => updateStyle('brandNameItalic', value)}
              onUppercaseChange={value => updateStyle('brandNameUppercase', value)}
            />
            <ContactCaptureTextStyleControls
              label="Header"
              disabled={busy}
              align={settings.styles.headerTextAlign}
              size={settings.styles.headerTextSize}
              sizeRange={CONTACT_CAPTURE_HEADER_TEXT_SIZE}
              bold={settings.styles.headerTextBold}
              italic={settings.styles.headerTextItalic}
              uppercase={settings.styles.headerTextUppercase}
              onAlignChange={value => updateStyle('headerTextAlign', value)}
              onSizeChange={value => updateStyle('headerTextSize', value)}
              onBoldChange={value => updateStyle('headerTextBold', value)}
              onItalicChange={value => updateStyle('headerTextItalic', value)}
              onUppercaseChange={value => updateStyle('headerTextUppercase', value)}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};

